import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../config';
import axios from 'axios';
import Swal from 'sweetalert2';
import TopLogo from '../assets/images/arka-saydam-logo.png';

const getAuthHeaders = () => {
  const token = localStorage.getItem('authToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const isRegistrationCompleted = (profile) => {
  const intake = profile?.intakeSurvey;
  if (!intake) return false;
  try {
    const parsed = typeof intake === 'string' ? JSON.parse(intake) : intake;
    const completedFlag = parsed?.completedRegistration === true;
    const hasAnswers = parsed?.answers && typeof parsed.answers === 'object' && Object.keys(parsed.answers).length > 0;
    return completedFlag || hasAnswers;
  } catch (_error) {
    return false;
  }
};

const OnboardingSetup = () => {
  const [modalStep, setModalStep] = useState(1); // 1: Seçim, 2: Önizleme
  const [previewItems, setPreviewItems] = useState([]);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [isGeneratingSmartKitchen, setIsGeneratingSmartKitchen] = useState(false);
  const [userProfileData, setUserProfileData] = useState(null);
  const [setupProgress, setSetupProgress] = useState(null);

  const saveSetupProgress = async (patch) => {
    try {
      const token = localStorage.getItem('authToken');
      const headers = {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      };
      const res = await fetch(`${API_BASE_URL}/api/profile/onboarding-progress`, {
        method: 'POST',
        credentials: 'include',
        headers,
        body: JSON.stringify(patch || {})
      });
      if (res.ok) {
        const data = await res.json().catch(() => ({}));
        if (data?.progress) setSetupProgress(data.progress);
      }
    } catch (_) {
      // best effort only
    }
  };

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const authToken = localStorage.getItem('authToken');
        const headers = authToken ? { Authorization: `Bearer ${authToken}` } : {};
        const res = await fetch(`${API_BASE_URL}/api/profile`, { credentials: 'include', headers });

        if (!mounted || !res.ok) return;
        const profile = await res.json();
        if (!isRegistrationCompleted(profile)) {
          window.location.href = '/registration-survey';
          return;
        }
        setUserProfileData(profile);
        const statusRes = await fetch(`${API_BASE_URL}/api/profile/status`, { credentials: 'include', headers });
        const status = statusRes.ok ? await statusRes.json() : null;

        if (status && status.completedRegistration && !status.shouldResumeOnboarding) {
          window.location.href = '/dashboard';
          return;
        }

        const progress = profile?.onboardingStates?.setupProgress || null;
        setSetupProgress(progress);

        if (progress && progress.completed !== true) {
          const resumeStep = Number(progress.step || 1);
          if (resumeStep === 2) {
            await handleFetchPreview(progress.profileId || null, true);
          } else {
            setModalStep(1);
          }
        } else {
          await saveSetupProgress({
            step: 1,
            mode: 'ready_list',
            profileId: getProfileIdFromSurvey(),
            completed: false
          });
        }
      } catch (err) {
        console.warn('Could not fetch profile for onboarding setup', err);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const getProfileIdFromSurvey = () => {
    let profileId = 'busy_worker';
    if (userProfileData?.intakeSurvey) {
      let surveyObj = userProfileData.intakeSurvey;
      if (typeof surveyObj === 'string') surveyObj = JSON.parse(surveyObj);

      const routine = surveyObj?.answers?.routine;
      if (routine?.includes('öğrenci')) profileId = 'student';
      else if (routine?.includes('emekli')) profileId = 'retired';
      else if (routine?.includes('ev ')) profileId = 'home_cook';
    }
    return profileId;
  };

  const handleFetchPreview = async (forcedProfileId = null, skipProgressWrite = false) => {
    setIsLoadingPreview(true);
    try {
      const profileId = forcedProfileId || getProfileIdFromSurvey();
      const res = await axios.get(`${API_BASE_URL}/api/inventory/onboarding-profile-items/${profileId}`, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      if (res.data.success) {
        setPreviewItems(res.data.items || []);
        setModalStep(2); // Go to Preview Step
        if (!skipProgressWrite) {
          await saveSetupProgress({
            step: 2,
            mode: 'ready_list',
            profileId,
            hasPreview: true,
            completed: false
          });
        }
      }
    } catch (error) {
      console.error("Preview fetch error:", error);
      Swal.fire({
        icon: 'error',
        title: 'Hata',
        text: 'Liste alınırken bir hata oluştu.'
      });
    } finally {
      setIsLoadingPreview(false);
    }
  };

  const handleSmartKitchenGenerate = async () => {
    setIsGeneratingSmartKitchen(true);
    try {
      const profileId = getProfileIdFromSurvey();
      const res = await axios.post(`${API_BASE_URL}/api/inventory/onboarding-seed`, { profileId }, {
        headers: getAuthHeaders(),
        withCredentials: true
      });

      if (res.data.success) {
        await saveSetupProgress({
          step: 2,
          mode: 'ready_list',
          profileId,
          completed: true,
          completedAt: new Date().toISOString()
        });
        Swal.fire({
          icon: 'success',
          title: 'Akıllı Mutfağınız Hazır! 🎉',
          text: `${res.data.count} malzeme başarıyla dolabınıza eklendi.`,
          timer: 2500,
          showConfirmButton: false
        }).then(() => {
          localStorage.setItem('onboarding_skipped', '1');
          window.location.href = '/smart-fridge';
        });
      }
    } catch (error) {
      console.error(error);
      Swal.fire({
        icon: 'error',
        title: 'Hata',
        // 🚀 [V8] Yönlendirme: Onboarding sonrası doğrudan Smart Search'e
        text: error?.response?.data?.error === 'inventory_already_seeded'
          ? 'Daha önce bir mutfak oluşturmuş görünüyorsunuz.'
          : 'Akıllı mutfak oluşturulurken bir hata meydana geldi.'
      });
      if (error?.response?.data?.error === 'inventory_already_seeded') {
        await saveSetupProgress({
          completed: true,
          completedAt: new Date().toISOString()
        });
        localStorage.setItem('onboarding_skipped', '1');
        window.location.href = '/smart-fridge'; // Changed from /dashboard to /smart-fridge
      }
    } finally {
      setIsGeneratingSmartKitchen(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 font-sans antialiased relative">
      {/* BEGIN: Modal Overlay */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-white sm:bg-black/40 backdrop-blur-sm" data-purpose="modal-overlay">
        {/* BEGIN: Main Modal Container */}
        <div className="relative w-full h-auto max-w-4xl bg-white rounded-3xl sm:rounded-[2.5rem] shadow-2xl animate-in zoom-in-95 duration-300 overflow-hidden" data-purpose="setup-modal">

          {/* Close/Dismiss removed as choice is mandatory */}

          {/* BEGIN: Modal Header with Logo */}
          <div className="pt-8 pb-4 text-center" data-purpose="modal-header">
            {/* Brand Logo */}
            <div className="flex justify-center mb-2">
              <div className="w-20 h-20 md:w-28 md:h-28 overflow-hidden">
                <img alt="BBTV Logo" className="w-full h-full object-contain" src={TopLogo} />
              </div>
            </div>
            {modalStep === 1 ? (
              <React.Fragment>
                <h1 className="text-2xl md:text-3xl font-extrabold text-gray-800 tracking-tight">
                  Mutfağını Kuralım
                </h1>
                <p className="mt-1 text-sm md:text-base text-gray-500 font-medium">Lütfen devam etmek için bir yöntem seçin</p>
              </React.Fragment>
            ) : (
              <React.Fragment>
                <h1 className="text-2xl md:text-3xl font-extrabold text-gray-800 tracking-tight flex items-center justify-center gap-3">
                  <button onClick={async () => {
                    setModalStep(1);
                    await saveSetupProgress({
                      step: 1,
                      mode: setupProgress?.mode || 'ready_list',
                      profileId: setupProgress?.profileId || getProfileIdFromSurvey(),
                      completed: false
                    });
                  }} className="p-2 hover:bg-gray-100 rounded-full transition-colors text-gray-400 hover:text-gray-600">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"></path></svg>
                  </button>
                  Hazırlanan Liste
                </h1>
                <p className="mt-1 text-sm md:text-base text-gray-500 font-medium">Senin için bu ürünleri seçtik, hızlıca göz at ve ekle</p>
              </React.Fragment>
            )}
          </div>
          {/* END: Modal Header with Logo */}

          {/* BEGIN: Modal Content Options (STEP 1) */}
          {modalStep === 1 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-6 md:p-8 pt-0 animate-in slide-in-from-right-8 duration-300" data-purpose="options-grid">

              {/* BEGIN: Option 1 - Ready List */}
              <div className="flex flex-col items-center p-6 bg-gray-50 rounded-[2rem] border-2 border-transparent hover:border-red-500 transition-all duration-300 group shadow-sm" data-purpose="option-ready-list">
                {/* Icon Container */}
                <div className="mb-4 p-3 bg-red-100 rounded-2xl text-red-600 group-hover:scale-110 transition-transform">
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"></path>
                  </svg>
                </div>
                <h2 className="text-lg font-bold text-gray-800 mb-2">Hazır Liste ile Başla</h2>
                <p className="text-center text-sm md:text-[15px] text-gray-600 mb-6 leading-relaxed">
                  Sizin için en sık kullanılan temel ürünlerden oluşan bir liste hazırladık. Dolabınızda olduğunu düşündüğümüz ürünleri hızlıca seçebilirsiniz.
                </p>
                <button
                  onClick={() => handleFetchPreview()}
                  disabled={isLoadingPreview}
                  className="mt-auto w-full py-4 px-6 bg-red-600 hover:bg-red-700 text-white font-bold rounded-xl transition-colors shadow-lg shadow-red-200 flex items-center justify-center gap-2"
                >
                  {isLoadingPreview ? <i className="fa-solid fa-spinner fa-spin"></i> : null}
                  Önerilenleri Gör
                </button>
              </div>
              {/* END: Option 1 */}

              {/* BEGIN: Option 2 - Manual Entry */}
              <div className="flex flex-col items-center p-6 bg-gray-50 rounded-[2rem] border-2 border-transparent hover:border-red-500 transition-all duration-300 group shadow-sm" data-purpose="option-manual-entry">
                {/* Icon Container */}
                <div className="mb-4 p-3 bg-red-100 rounded-2xl text-red-600 group-hover:scale-110 transition-transform">
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
                  </svg>
                </div>
                <h2 className="text-lg font-bold text-gray-800 mb-2">Kendi Ürünlerimi Tanımlayım</h2>
                <p className="text-center text-sm md:text-[15px] text-gray-600 mb-6 leading-relaxed">
                  Kendi mutfak envanterinizi sıfırdan oluşturmak ve malzemelerinizi tek tek tanımlamak istiyorsanız bu adımla devam edin.
                </p>
                <button
                  onClick={async () => {
                    await saveSetupProgress({
                      step: 1,
                      mode: 'manual',
                      profileId: getProfileIdFromSurvey(),
                      completed: false
                    });
                    window.location.href = '/wizard';
                  }}
                  className="mt-auto w-full py-3 px-6 bg-white border-2 border-red-600 text-red-600 hover:bg-red-50 font-bold rounded-xl transition-colors"
                >
                  Manuel Giriş Yap
                </button>
              </div>
              {/* END: Option 2 */}

            </div>
          )}
          {/* END: Modal Content Options */}

          {/* BEGIN: Step 2 Preview Content */}
          {modalStep === 2 && (
            <div className="flex flex-col p-6 md:p-8 pt-0 animate-in slide-in-from-right-8 duration-300 w-full lg:w-4/5 mx-auto">
              <div className="flex-1 overflow-y-auto mb-4 pr-2 custom-scrollbar space-y-2" style={{ maxHeight: '300px' }}>
                {previewItems.map((item, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-2xl border border-gray-100">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-white shadow-sm flex items-center justify-center text-xl">
                        {item.icon_url ? <img src={item.icon_url} className="w-6 h-6 object-contain" /> : '🍲'}
                      </div>
                      <div>
                        <h5 className="font-bold text-sm text-gray-900 capitalize">{item.name}</h5>
                        <p className="text-[10px] sm:text-xs text-gray-500">{item.category}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <span className="inline-block px-2 py-1 bg-white rounded-lg text-xs font-semibold text-gray-700 border border-gray-100">
                        {item.quantity} {item.unit}
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-auto pt-3 border-t border-gray-100">
                <button
                  onClick={handleSmartKitchenGenerate}
                  disabled={isGeneratingSmartKitchen}
                  className="w-full bg-red-600 hover:bg-red-700 text-white py-3 px-6 rounded-2xl font-bold text-base sm:text-lg shadow-xl shadow-red-200 transition-all duration-300 flex items-center justify-center gap-3">
                  {isGeneratingSmartKitchen ? <i className="fa-solid fa-spinner fa-spin"></i> : <><span className="text-xl">✨</span> Harika, Hemen Ekle</>}
                </button>
                <p className="text-center text-[10px] sm:text-xs text-gray-400 mt-3">Bu listeyi daha sonra dilediğin gibi düzenleyebilirsin.</p>
              </div>
            </div>
          )}
          {/* END: Step 2 Preview Content */}

        </div>
      </div>
    </div>
  );
};

export default OnboardingSetup;
